import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Divider, IconButton, Menu, MenuItem } from "@mui/material";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import {
  MIDDLE_MILE_COLUMN_NAME,
  MIDDLE_MILE_LABEL_NAME,
} from "./MiddleMileConstants";
import { MiddleMileResponse } from "../../app/models/responseModels/middleMileResponse";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { useSelector } from "react-redux";
import {
  downloadMiddleMileDataToCsv,
  downloadMiddleMileDataToXlsx,
  getMiddleMileList,
} from "../../app/features/middleMile/middleMileSlice";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { downloadCsv, downloadXlsx } from "../../helpers/downloadFile";
import MiddleMileDataFilter from "./MiddleMileDataFilter";
import { useSubmitModalWithParameter } from "../../components/common/modalPopup/useSubmitModal";
import EditMiddleMileModal from "./modals/EditMiddleMileModal";

const breadcrumb = ["Middle Mile"];

const MiddleMile = () => {
  const dispatch = useAppDispatch();
  const { isLoading, middleMileList } = useSelector(
    (state: RootState) => state.middleMile
  );

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [filteredData, setFilteredData] = useState<MiddleMileResponse[]>([]);

  // ***** Callbacks *****
  const getMiddleMileData = useCallback(async () => {
    try {
      const response = await dispatch(getMiddleMileList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "middleMileList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "middleMileListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getMiddleMileData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getMiddleMileData]);

  useEffect(() => {
    if (middleMileList.length > 0) {
      if (filteredData.length === middleMileList.length) {
        setFilteredData(middleMileList);
      }
    } else {
      setFilteredData([]);
    }
  }, [middleMileList, filteredData]);

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<MiddleMileResponse>[]>(
    () => [
      {
        accessorKey: MIDDLE_MILE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME,
        header: MIDDLE_MILE_LABEL_NAME.PARCEL_CENTER_LOCATION_NAME,
        size: 150,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: MIDDLE_MILE_COLUMN_NAME.CLIENT_NAME,
        header: MIDDLE_MILE_LABEL_NAME.CLIENT_NAME,
        size: 180,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: MIDDLE_MILE_COLUMN_NAME.EXTERNAL_PARTNER_REGION,
        header: MIDDLE_MILE_LABEL_NAME.EXTERNAL_PARTNER_REGION,
        size: 200,
      },
      {
        accessorKey: MIDDLE_MILE_COLUMN_NAME.TRACKING_TYPE,
        header: MIDDLE_MILE_LABEL_NAME.TRACKING_TYPE,
        size: 80,
      },
      {
        filterVariant: "date",
        accessorKey: MIDDLE_MILE_COLUMN_NAME.EXPECTED_ARRIVAL,
        accessorFn: (row) => new Date(row.expectedArrival),
        header: MIDDLE_MILE_LABEL_NAME.EXPECTED_ARRIVAL,
        size: 200,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.expectedArrival ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleDateString()}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: MIDDLE_MILE_COLUMN_NAME.TRACKING_NUMBER,
        header: MIDDLE_MILE_LABEL_NAME.TRACKING_NUMBER,
        size: 230,
      },
      {
        accessorKey: MIDDLE_MILE_COLUMN_NAME.PACKAGES_COUNT,
        header: MIDDLE_MILE_LABEL_NAME.PACKAGES_COUNT,
        size: 80,
      },
      {
        filterVariant: "date",
        accessorKey: MIDDLE_MILE_COLUMN_NAME.CONTAINER_RECEIVED,
        accessorFn: (row) => new Date(row.containerReceived),
        header: MIDDLE_MILE_LABEL_NAME.CONTAINER_RECEIVED,
        size: 230,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.containerReceived ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: MIDDLE_MILE_COLUMN_NAME.RECEIVING_STARTED,
        filterVariant: "date",
        accessorFn: (row) => new Date(row.receivingStarted),
        header: MIDDLE_MILE_LABEL_NAME.RECEIVING_STARTED,
        size: 200,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.receivingStarted ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: MIDDLE_MILE_COLUMN_NAME.RECEIVING_ENDED,
        filterVariant: "date",
        accessorFn: (row) => new Date(row.receivingEnded),
        header: MIDDLE_MILE_LABEL_NAME.RECEIVING_ENDED,
        size: 200,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.receivingEnded ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: MIDDLE_MILE_COLUMN_NAME.NOTES,
        header: MIDDLE_MILE_LABEL_NAME.NOTES,
        size: 200,
      },
    ],
    []
  );

  // ***** Handlers *****
  const handleRefresh = () => {
    getMiddleMileData();
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadMiddleMileDataToCsv(filteredData)
      ).unwrap();

      if (response.isSuccess) {
        downloadCsv(response.data);
      } else {
        toast.error(response.message!, {
          toastId: "middleMileDownloadCsv",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "middleMileDownloadCsvException",
      });
    }
    handleClose();
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadMiddleMileDataToXlsx(filteredData)
      ).unwrap();

      if (response) {
        downloadXlsx(response, "MiddleMile.xlsx");
      } else {
        toast.error("unable to download file", {
          toastId: "middleMileDownloadXlsx",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "middleMileDownloadXlsxException",
      });
    }
  };

  // ***** Modal Popups *****
  const [editMiddleMileModal, openEditMiddleMileModal] =
    useSubmitModalWithParameter<MiddleMileResponse>(
      (middleMileRowData, closeEditMiddleMileModal) => (
        <EditMiddleMileModal
          closeEditMiddleMileModal={closeEditMiddleMileModal}
          onCompleted={getMiddleMileData}
          middleMileRowData={middleMileRowData}
        />
      )
    );

  // ***** Datagrid Custom Actions *****

  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<MiddleMileResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={handleRefresh}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <IconButton type="button" onClick={handleDownloadMenuClick}>
            <DownloadIcon sx={{ color: "blue" }} />
          </IconButton>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            Csv
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            Xlsx
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      <>
        <MiddleMileDataFilter
          middleMileData={middleMileList}
          setFilteredData={setFilteredData}
          filteredData={filteredData}
        />
        <Divider />
        <br />
      </>
      <CommandOpsDataGrid
        data={filteredData}
        columns={columns}
        isLoading={isLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        emptyRowMessage={"No middle mile packages"}
        rowCount={filteredData.length.toLocaleString()}
        isDescending={true}
        defaultSortColumnName="receivingStarted"
        openModal={openEditMiddleMileModal}
      />
      {editMiddleMileModal}
    </Box>
  );
};

export default MiddleMile;
